import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, SpaceBetween } from '@amzn/awsui-components-react';
import ErrorUtils from 'src/utils/ErrorUtils';
import { ApprovalType } from 'src/models/multidimensional-allocation/ProcessApprovals';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { downloadBlockedReason, submitBlockedReason } from 'src/services/derived-allocation-sheet/dasSheetActions';
import { useApprovalButtonProps } from 'src/components/multidimensional-allocation/hooks/useApprovalButtonProps';
import { ProcessApprovalModal } from 'src/components/multidimensional-allocation/shared/ProcessApprovalModal';

/**
 * Sheet actions under the builder's steps, with the manual allocation sheet's buttons: Download the open version's
 * output, and Submit for Review on a draft or Approval Decision on a sheet under review. Submit waits until the
 * draft is saved and its current content has been executed. Submit and the approval decision are offered only on
 * the working version to users who can edit.
 */
export default function DasSheetActions(props: {
    sheet: DerivedAllocationSheetData,
    canEdit: boolean,
    viewingWorkingVersion: boolean,
    /** Called after a submit, approve or reject, to open the working version again (an approve moves the draft to live). */
    onApprovalProcessed: () => void,
}) {
    const { sheet, canEdit, viewingWorkingVersion, onApprovalProcessed } = props;
    const { dasState, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    const [modalMode, setModalMode] = useState<'closed' | 'submit' | 'approval'>('closed');
    const [downloading, setDownloading] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const approvalButton = useApprovalButtonProps(sheet.status, () => setModalMode('submit'), () => setModalMode('approval'),
        undefined, () => submitBlockedReason(sheet, dasState.unsavedSteps.length > 0));
    const downloadReason = downloadBlockedReason(sheet);

    /** Opens the output of the open version through a one-time link. */
    const download = async () => {
        setDownloading(true);
        try {
            const { downloadUrl } = await services.derivedAllocationSheetService.downloadDerivedAllocationSheet({ sheetId, version: sheet.sheetVersion });
            if (downloadUrl) {
                // A link click, as the segment table toolbar does: the URL arrives after the fetch, outside the click's
                // user activation, so window.open could be popup-blocked. A new tab keeps the builder open.
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                services.messageService.showErrorBanner('The download link could not be created. Try again.');
            }
        } catch (ex) {
            services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) setDownloading(false);
        }
    };

    return (
        <SpaceBetween direction="horizontal" size="xs">
            <Button data-class="dasDownloadButton" iconName="download" onClick={download} loading={downloading}
                    disabled={!!downloadReason || downloading} disabledReason={downloadReason}>
                Download
            </Button>
            {canEdit && viewingWorkingVersion && (
                <>
                    <Button data-class="dasApprovalButton" variant="primary" onClick={approvalButton.onClick}
                            disabled={approvalButton.disabled} disabledReason={approvalButton.disabledReason}>
                        {approvalButton.label}
                    </Button>
                    <ProcessApprovalModal entityId={sheetId} approvalType={ApprovalType.DERIVED_ALLOCATION_SHEET} mode={modalMode}
                                          onModeChange={setModalMode} onSuccess={onApprovalProcessed} />
                </>
            )}
        </SpaceBetween>
    );
}
